/**
 * AI Sidebar Context
 *
 * Global state for AI chat sidebar visibility that persists across page navigation.
 * Uses localStorage to remember state across browser sessions.
 * Also tracks current page context for the global sidebar.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface PageContext {
  pageId: string;
  onSuccess?: (toolCalls: any[]) => void;
}

interface AISidebarContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  toggleSidebar: () => void;
  currentPage: PageContext | null;
  setCurrentPage: (page: PageContext) => void;
}

const AISidebarContext = createContext<AISidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'ai-sidebar-open';

interface AISidebarProviderProps {
  children: ReactNode;
}

/**
 * Provider component for AI sidebar state
 */
export function AISidebarProvider({ children }: AISidebarProviderProps) {
  // Initialize from localStorage (default to false)
  const [isOpen, setIsOpenState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'true';
    } catch (error) {
      console.error('Failed to read AI sidebar state from localStorage:', error);
      return false;
    }
  });

  // Track current page context (pageId and onSuccess callback)
  const [currentPage, setCurrentPage] = useState<PageContext | null>(null);

  // Sync to localStorage whenever state changes
  const setIsOpen = useCallback((newValue: boolean) => {
    setIsOpenState(newValue);
    try {
      localStorage.setItem(STORAGE_KEY, String(newValue));
    } catch (error) {
      console.error('Failed to save AI sidebar state to localStorage:', error);
    }
  }, []);

  // Toggle helper — uses functional update so it doesn't depend on `isOpen`
  const toggleSidebar = useCallback(() => {
    setIsOpenState(prev => {
      const newValue = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(newValue));
      } catch (error) {
        console.error('Failed to save AI sidebar state to localStorage:', error);
      }
      return newValue;
    });
  }, []);

  const contextValue = useMemo<AISidebarContextType>(() => ({
    isOpen,
    setIsOpen,
    toggleSidebar,
    currentPage,
    setCurrentPage
  }), [isOpen, setIsOpen, toggleSidebar, currentPage]);

  return (
    <AISidebarContext.Provider value={contextValue}>
      {children}
    </AISidebarContext.Provider>
  );
}

/**
 * Hook to access AI sidebar state
 *
 * @example
 * ```tsx
 * const { isOpen, setIsOpen, toggleSidebar, currentPage } = useAISidebar();
 * ```
 */
export function useAISidebar() {
  const context = useContext(AISidebarContext);
  if (context === undefined) {
    throw new Error('useAISidebar must be used within AISidebarProvider');
  }
  return context;
}
